import { Capacitor } from '@capacitor/core';
const STORAGE_KEY = 'student-system:system-time';
const CONTROL_ID = 'student-system-time-control';
const PANEL_ID = 'student-system-time-panel';
const VALID_MODES =
  new Set([
    'real',
    'open',
    'simulated'
  ]);

const VALID_HONORS_TEST_STATUSES =
  new Set([
    'Honors',
    'Affiliate',
    'Applicant',
    'Non-Honors'
  ]);


function normalizeHonorsTestStatus(
  value
) {
  const clean =
    String(
      value || ''
    ).trim();

  return VALID_HONORS_TEST_STATUSES.has(
    clean
  )
    ? clean
    : null;
}

function readJson(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function isSessionCurrent(session) {
  if (!session) return false;
  if (!session.expiresAt) return true;
  return Date.now() <= Number(session.expiresAt);
}

function getLocalStudentSession() {
  const session = readJson('studentSession');
  return isSessionCurrent(session) ? session : null;
}

function getLocalCmsSession() {
  const session = readJson('cmsSession');
  return isSessionCurrent(session) ? session : null;
}

function getMasterSession(session = null) {
  if (session?.isMaster && isSessionCurrent(session)) return session;
  const stored = getLocalStudentSession();
  return stored?.isMaster ? stored : null;
}

function testingIsAuthorised(session = null) {
  if (typeof window === 'undefined') {
    return false;
  }

  const path = window.location.pathname;

  if (path.startsWith('/cms')) {
    return (
      getLocalCmsSession()?.isSuperadmin === true
    );
  }

  if (path.startsWith('/student')) {
    return (
      session?.isMaster === true ||
      getMasterSession(session)?.isMaster === true
    );
  }

  return false;
}

function readState() {
  const emptyState = {
    mode: null,
    simulatedTime: null,
    honorsStatus: null
  };


  if (
    typeof window ===
    'undefined'
  ) {
    return emptyState;
  }


  try {
    const raw =
      window.localStorage.getItem(
        STORAGE_KEY
      );


    if (!raw) {
      return emptyState;
    }


    const parsed =
      JSON.parse(raw);


    return {
      mode:
        VALID_MODES.has(
          parsed?.mode
        )
          ? parsed.mode
          : null,

      simulatedTime:
        typeof parsed?.simulatedTime ===
          'string'
          ? parsed.simulatedTime
          : null,

      honorsStatus:
        normalizeHonorsTestStatus(
          parsed?.honorsStatus
        )
    };

  } catch (error) {
    return emptyState;
  }
}

function writeState(state) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function validSimulatedDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatLocalDate(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function toDateTimeLocalValue(date) {
  return [
    formatLocalDate(date),
    [
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0')
    ].join(':')
  ].join('T');
}

function formatControlDate(date) {
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getSystemTimeMode(session = null) {
  if (!testingIsAuthorised(session)) return 'real';

  const state = readState();
  if (state.mode) return state.mode;

  const master = getMasterSession(session);
  if (master?.testingMode) return 'open';

  return 'real';
}

export function getEffectiveNow(session = null) {
  if (getSystemTimeMode(session) === 'simulated') {
    const simulated = validSimulatedDate(readState().simulatedTime);
    if (simulated) return new Date(simulated.getTime());
  }

  return new Date();
}

export function getEffectiveToday(session = null) {
  return formatLocalDate(getEffectiveNow(session));
}

export function isOpenEverything(session = null) {
  const master = getMasterSession(session);
  if (!master) return false;
  return getSystemTimeMode(master) === 'open';
}

export function getSystemTimeState(session = null) {
  const stored =
    readState();

  return {
    mode:
      getSystemTimeMode(
        session
      ),

    simulatedTime:
      stored.simulatedTime,

    honorsStatus:
      getMasterSession(
        session
      )
        ? stored.honorsStatus
        : null
  };
}


export function getHonorsTestingContext(
  session = null
) {
  const master =
    getMasterSession(
      session
    );


  if (!master) {
    return null;
  }


  const state =
    getSystemTimeState(
      master
    );


  let effectiveAt =
    null;


  if (
    state.mode ===
    'simulated'
  ) {
    const simulated =
      validSimulatedDate(
        state.simulatedTime
      );


    if (simulated) {
      effectiveAt =
        simulated.toISOString();
    }
  }


  return {
    mode:
      state.mode,

    effectiveAt,

    openEverything:
      state.mode ===
      'open',

    honorsStatus:
      state.honorsStatus ||
      null
  };
}

export function setSystemTime(
  mode,
  simulatedTime = null,
  session = null,
  honorsStatus = undefined
) {
  if (
    session?.isMaster !== true &&
    !testingIsAuthorised(
      session
    )
  ) {
    throw new Error(
      'Testing time is not available for this session.'
    );
  }


  if (
    !VALID_MODES.has(
      mode
    )
  ) {
    throw new Error(
      'Invalid testing-time mode.'
    );
  }


  if (
    mode === 'open' &&
    !getMasterSession(
      session
    )
  ) {
    throw new Error(
      'Open Everything is available only to the master student account.'
    );
  }


  if (
    mode === 'simulated' &&
    !validSimulatedDate(
      simulatedTime
    )
  ) {
    throw new Error(
      'Choose a valid simulated date and time.'
    );
  }


  const previous =
    readState();


  const nextHonorsStatus =
    honorsStatus ===
      undefined
      ? previous.honorsStatus
      : normalizeHonorsTestStatus(
          honorsStatus
        );


  writeState({
    mode,

    simulatedTime:
      mode === 'simulated'
        ? simulatedTime
        : null,

    honorsStatus:
      nextHonorsStatus
  });
}

export function clearSystemTimePreference() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

function removeControl() {
  if (typeof document === 'undefined') return;
  document.getElementById(CONTROL_ID)?.remove();
  document.getElementById(PANEL_ID)?.remove();
}

function buttonLabel(mode, simulatedTime) {
  if (mode === 'open') return '🧪 Open Everything';
  if (mode === 'simulated') {
    const date = validSimulatedDate(simulatedTime);
    return date ? `🧪 ${formatControlDate(date)}` : '🧪 Simulated Time';
  }
  return '🧪 Testing: Real Time';
}

function closePanel() {
  if (typeof document === 'undefined') return;
  document.getElementById(PANEL_ID)?.remove();
}

function makeModeOption(value, label, selected) {
  const option = document.createElement('option');
  option.value = value;
  option.textContent = label;
  option.selected = selected;
  return option;
}

function openPanel(button) {
  closePanel();

  const master =
  window.location.pathname.startsWith('/student')
    ? getMasterSession()
    : null;
  const state = getSystemTimeState(master);

  const panel = document.createElement('div');
  panel.id = PANEL_ID;
  Object.assign(panel.style, {
    position: 'fixed',
    right: '16px',
    bottom: '64px',
    zIndex: '2147483647',
    width: '320px',
    maxWidth: 'calc(100vw - 32px)',
    background: '#fff',
    border: '2px solid #b45309',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
    padding: '14px',
    fontFamily: 'Arial, sans-serif',
    color: '#1f2933'
  });

  const title = document.createElement('div');
  title.textContent = 'Testing Time';
  Object.assign(title.style, {
    fontSize: '16px',
    fontWeight: '800',
    marginBottom: '4px'
  });
  panel.appendChild(title);

  const note = document.createElement('div');
  note.textContent = master
    ? 'Controls how time-dependent student behaviour is evaluated.'
    : 'Controls how time-dependent instructor behaviour is evaluated.';
  Object.assign(note.style, {
    fontSize: '12px',
    lineHeight: '1.4',
    color: '#66788a',
    marginBottom: '12px'
  });
  panel.appendChild(note);

  const modeLabel = document.createElement('label');
  modeLabel.textContent = 'Mode';
  Object.assign(modeLabel.style, {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '4px'
  });
  panel.appendChild(modeLabel);

  const select = document.createElement('select');
  Object.assign(select.style, {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 9px',
    border: '1px solid #cfd8df',
    borderRadius: '7px',
    background: '#fff',
    fontSize: '13px',
    marginBottom: '10px'
  });
  select.appendChild(makeModeOption('real', 'Real Time', state.mode === 'real'));
  if (master) {
    select.appendChild(makeModeOption('open', 'Open Everything', state.mode === 'open'));
  }
  select.appendChild(makeModeOption('simulated', 'Simulated Date & Time', state.mode === 'simulated'));
  panel.appendChild(select);

  const dateWrap = document.createElement('div');
  const dateLabel = document.createElement('label');
  dateLabel.textContent = 'Simulated date and time';
  Object.assign(dateLabel.style, {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '4px'
  });
  dateWrap.appendChild(dateLabel);

  const dateInput = document.createElement('input');
  dateInput.type = 'datetime-local';
  const storedDate = validSimulatedDate(state.simulatedTime);
  dateInput.value = storedDate
    ? toDateTimeLocalValue(storedDate)
    : toDateTimeLocalValue(new Date());
  Object.assign(dateInput.style, {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 9px',
    border: '1px solid #cfd8df',
    borderRadius: '7px',
    fontSize: '13px'
  });
  dateWrap.appendChild(dateInput);
  panel.appendChild(dateWrap);

  const honorsStatusWrap =
    document.createElement(
      'div'
    );

  honorsStatusWrap.style.marginTop =
    '10px';


  const honorsStatusLabel =
    document.createElement(
      'label'
    );

  honorsStatusLabel.textContent =
    'Honors identity';

  Object.assign(
    honorsStatusLabel.style,
    {
      display:
        'block',

      fontSize:
        '12px',

      fontWeight:
        '700',

      marginBottom:
        '4px'
    }
  );

  honorsStatusWrap.appendChild(
    honorsStatusLabel
  );


  const honorsStatusSelect =
    document.createElement(
      'select'
    );

  Object.assign(
    honorsStatusSelect.style,
    {
      width:
        '100%',

      boxSizing:
        'border-box',

      padding:
        '8px 9px',

      border:
        '1px solid #cfd8df',

      borderRadius:
        '7px',

      background:
        '#fff',

      fontSize:
        '13px'
    }
  );


  [
    [
      '',
      'Actual status'
    ],
    [
      'Honors',
      'Honors'
    ],
    [
      'Affiliate',
      'Affiliate'
    ],
    [
      'Applicant',
      'Applicant'
    ],
    [
      'Non-Honors',
      'Non-Honors'
    ]
  ].forEach(
    ([
      value,
      label
    ]) => {
      const option =
        document.createElement(
          'option'
        );

      option.value =
        value;

      option.textContent =
        label;

      option.selected =
        (
          state.honorsStatus ||
          ''
        ) ===
        value;

      honorsStatusSelect
        .appendChild(
          option
        );
    }
  );


  honorsStatusWrap.appendChild(
    honorsStatusSelect
  );


  if (master) {
    panel.appendChild(
      honorsStatusWrap
    );
  }


  function updateDateVisibility() {
    dateWrap.style.display = select.value === 'simulated' ? 'block' : 'none';
  }
  select.addEventListener('change', updateDateVisibility);
  updateDateVisibility();

  const warning = document.createElement('div');
  warning.textContent = master
    ? 'Simulated time and Honors identity apply only to Master Student testing. Audit timestamps, logins and token expiry remain on real time.'
    : 'This changes availability/status calculations only. Audit timestamps, logins and OTP expiry remain on real time.';
  Object.assign(warning.style, {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '7px',
    padding: '8px',
    marginTop: '10px',
    fontSize: '11px',
    lineHeight: '1.4',
    color: '#9a3412'
  });
  panel.appendChild(warning);

  const error = document.createElement('div');
  Object.assign(error.style, {
    display: 'none',
    marginTop: '8px',
    fontSize: '12px',
    color: '#a33'
  });
  panel.appendChild(error);

  const actions = document.createElement('div');
  Object.assign(actions.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '12px'
  });

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';
  Object.assign(cancel.style, {
    border: '1px solid #cfd8df',
    background: '#fff',
    borderRadius: '7px',
    padding: '7px 10px',
    cursor: 'pointer'
  });
  cancel.addEventListener('click', closePanel);
  actions.appendChild(cancel);

  const apply = document.createElement('button');
  apply.type = 'button';
  apply.textContent = 'Apply';
  Object.assign(apply.style, {
    border: 'none',
    background: '#b45309',
    color: '#fff',
    borderRadius: '7px',
    padding: '8px 12px',
    fontWeight: '700',
    cursor: 'pointer'
  });
  apply.addEventListener('click', () => {
    try {
      const selectedMode = select.value;
      setSystemTime(
        selectedMode,

        selectedMode ===
          'simulated'
          ? dateInput.value
          : null,

        master,

        master
          ? (
              honorsStatusSelect
                .value ||
              null
            )
          : undefined
      );
      window.location.reload();
    } catch (err) {
      error.textContent = err?.message || 'Could not change testing time.';
      error.style.display = 'block';
    }
  });
  actions.appendChild(apply);

  panel.appendChild(actions);
  document.body.appendChild(panel);

  const rect = panel.getBoundingClientRect();
  if (rect.top < 8) {
    panel.style.bottom = '8px';
  }

  button.setAttribute('aria-expanded', 'true');
}

export function ensureSystemTimeControl() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!document.body) return;

  if (Capacitor.isNativePlatform()) {
    removeControl();
    return;
  }

  if (!testingIsAuthorised()) {
    removeControl();
    return;
  }

  const state = getSystemTimeState();
  let button = document.getElementById(CONTROL_ID);

  if (!button) {
    button = document.createElement('button');
    button.id = CONTROL_ID;
    button.type = 'button';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    Object.assign(button.style, {
      position: 'fixed',
      right: '16px',
      bottom: '16px',
      zIndex: '2147483647',
      border: '2px solid #b45309',
      background: '#fff7ed',
      color: '#9a3412',
      borderRadius: '999px',
      padding: '9px 13px',
      fontSize: '12px',
      fontWeight: '800',
      boxShadow: '0 4px 16px rgba(0,0,0,0.16)',
      cursor: 'pointer',
      fontFamily: 'Arial, sans-serif'
    });

    button.addEventListener('click', () => {
      const existing = document.getElementById(PANEL_ID);
      if (existing) {
        closePanel();
        button.setAttribute('aria-expanded', 'false');
      } else {
        openPanel(button);
      }
    });

    document.body.appendChild(button);
  }

  button.textContent = buttonLabel(state.mode, state.simulatedTime);
  button.style.background = state.mode === 'simulated' ? '#fef3c7' : '#fff7ed';
}

function bootstrapControl() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const run = () => ensureSystemTimeControl();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  window.addEventListener('storage', event => {
    if ([STORAGE_KEY, 'studentSession', 'cmsSession'].includes(event.key)) {
      run();
    }
  });

  // Login/logout writes localStorage in the same tab, which does not fire a
  // storage event. A light periodic check keeps the control in sync.
  window.setInterval(run, 2000);
}

bootstrapControl();